import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DownloadOutlined, ExpandOutlined, LoadingOutlined } from '@ant-design/icons';
import { Checkbox, Image, Modal, Spin, Tooltip, message } from 'antd';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Utils from '@utils/utils';
import { downloadFlowOutputImages, getFlowProjectHistoryImages } from '../../execution';
import styles from './index.module.scss';

const BASE_URL = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas';
const SEND_ICON = `${BASE_URL}/send.svg`;
const DOWNLOAD_ICON = `${BASE_URL}/download.svg`;
const APPLY_TO_CANVAS_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-web/admin/freeCanvas/apply.svg';
const PAGE_SIZE = 50;

const getItemKey = (item) => `${item.executionId || ''}-${item.taskId || ''}-${item.url}`;

const MIME_EXTENSION_MAP = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/svg+xml': 'svg',
};

const getExtensionFromBlob = (blob, fallbackUrl = '') => {
    const mimeExtension = MIME_EXTENSION_MAP[blob?.type];
    if (mimeExtension) return mimeExtension;

    const urlPath = fallbackUrl.split('?')[0] || '';
    const lastSegment = urlPath.split('/').pop() || '';
    const urlExtension = lastSegment.includes('.') ? lastSegment.split('.').pop() : '';
    return urlExtension || 'png';
};

const getDownloadFileName = (url, blob, prefix = 'history-image') => {
    const urlPath = url.split('?')[0] || '';
    const lastSegment = urlPath.split('/').pop() || '';
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(lastSegment);

    if (lastSegment && hasExtension) {
        return lastSegment;
    }

    return `${prefix}-${Date.now()}.${getExtensionFromBlob(blob, url)}`;
};

const formatGroupDate = (value) => {
    if (!value) return '未知日期';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '未知日期';

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const groupHistoryItems = (items) => {
    const groups = [];
    const groupMap = new Map();

    items.forEach((item) => {
        const key = formatGroupDate(item?.createdTime);
        if (!groupMap.has(key)) {
            const group = { key, title: key, items: [] };
            groupMap.set(key, group);
            groups.push(group);
        }

        groupMap.get(key).items.push(item);
    });

    return groups;
};

const getHistoryItemOutputId = (item) => (
    item?.outputId || item?.id || ''
);

const isRealOutputId = (outputId) => (
    !!outputId && !String(outputId).startsWith('result-')
);

const triggerDownloadByUrl = async (url) => {
    if (!url) return;

    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = getDownloadFileName(url, blob);
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};

const resolveDownloadUrlsByItems = async (projectId, items = []) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    const resolvedUrls = items.map((item) => item?.url || '');
    if (!projectId) return resolvedUrls;

    const resolvableItems = items
        .map((item, index) => ({
            index,
            outputId: getHistoryItemOutputId(item),
        }))
        .filter((item) => isRealOutputId(item.outputId));

    if (!resolvableItems.length) return resolvedUrls;

    try {
        const apiUrls = await downloadFlowOutputImages(
            projectId,
            resolvableItems.map((item) => item.outputId),
        );

        resolvableItems.forEach((item, index) => {
            if (apiUrls[index]) {
                resolvedUrls[item.index] = apiUrls[index];
            }
        });
    } catch (error) {
        console.error('downloadFlowOutputImages error', error);
    }

    return resolvedUrls;
};

const downloadZip = async (projectId, selectedItems) => {
    const zip = new JSZip();
    const resolvedUrls = await resolveDownloadUrlsByItems(projectId, selectedItems);

    await Promise.all(selectedItems.map(async (item, index) => {
        const downloadUrl = resolvedUrls[index] || item?.url || '';
        if (!downloadUrl) return;

        const response = await fetch(downloadUrl);
        if (!response.ok) {
            throw new Error('下载图片失败');
        }

        const blob = await response.blob();
        const extension = getExtensionFromBlob(blob, downloadUrl);
        const fileName = `${item.nodeId || 'history'}-${index + 1}.${extension}`;
        zip.file(fileName, blob);
    }));

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `生成记录-${Date.now()}.zip`);
};

const GenerationHistoryModal = ({
    open = false,
    projectId = '',
    onClose,
    onSend,
    onBatchSend,
}) => {
    const scrollRef = useRef(null);
    const requestIdRef = useRef(0);
    const loadingRef = useRef(false);
    const [items, setItems] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [batchMode, setBatchMode] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [batchDownloading, setBatchDownloading] = useState(false);
    const [batchApplying, setBatchApplying] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(-1);

    const loadHistory = useCallback(async ({ nextPageIndex = 1, append = false } = {}) => {
        if (!projectId || loadingRef.current) return;

        const requestId = Date.now();
        requestIdRef.current = requestId;
        loadingRef.current = true;
        setLoading(true);

        try {
            const pageData = await getFlowProjectHistoryImages({
                projectId,
                pageIndex: nextPageIndex,
                pageSize: PAGE_SIZE,
            });

            if (requestIdRef.current !== requestId) return;

            const nextItems = (pageData.items || [])
                .slice()
                .sort((left, right) => (
                    new Date(right?.createdTime || 0).getTime() - new Date(left?.createdTime || 0).getTime()
                ));

            setItems((prev) => (append ? prev.concat(nextItems) : nextItems));
            setPageIndex(nextPageIndex);
            setHasMore(nextPageIndex < (pageData.totalPages || 0) && nextItems.length >= PAGE_SIZE);
            setInitialized(true);
        } catch (error) {
            console.error(error);
        } finally {
            if (requestIdRef.current === requestId) {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    }, [projectId]);

    useEffect(() => {
        if (!open) return;

        setItems([]);
        setPageIndex(1);
        setHasMore(true);
        setInitialized(false);
        setBatchMode(false);
        setSelectedKeys([]);
        loadHistory({ nextPageIndex: 1, append: false });
    }, [loadHistory, open, projectId]);

    useEffect(() => {
        if (!open) {
            requestIdRef.current = 0;
            loadingRef.current = false;
            setLoading(false);
            setBatchMode(false);
            setSelectedKeys([]);
            setBatchDownloading(false);
            setBatchApplying(false);
            setPreviewIndex(-1);
        }
    }, [open]);

    const handleScroll = useCallback((event) => {
        const target = event.currentTarget;
        const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;
        if (!nearBottom || loading || !hasMore) return;

        loadHistory({
            nextPageIndex: pageIndex + 1,
            append: true,
        });
    }, [hasMore, loadHistory, loading, pageIndex]);

    const groupedItems = useMemo(() => groupHistoryItems(items), [items]);
    const totalKeys = useMemo(() => items.map(getItemKey), [items]);
    const selectedItems = useMemo(() => (
        items.filter((item) => selectedKeys.includes(getItemKey(item)))
    ), [items, selectedKeys]);
    const previewUrls = useMemo(() => items.map((item) => item?.url).filter(Boolean), [items]);

    const handleToggleSelect = useCallback((itemKey) => {
        setSelectedKeys((prev) => (
            prev.includes(itemKey)
                ? prev.filter((key) => key !== itemKey)
                : prev.concat(itemKey)
        ));
    }, []);

    const handleToggleBatchMode = useCallback(() => {
        setBatchMode(true);
        setSelectedKeys([]);
    }, []);

    const handleCancelBatchMode = useCallback(() => {
        setBatchMode(false);
        setSelectedKeys([]);
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedKeys((prev) => (
            prev.length === totalKeys.length ? [] : totalKeys
        ));
    }, [totalKeys]);

    const handleBatchDownload = useCallback(async () => {
        if (batchDownloading) return;

        if (!selectedItems.length) {
            message.warning('请先选择图片');
            return;
        }

        try {
            setBatchDownloading(true);
            await downloadZip(projectId, selectedItems);
        } catch (error) {
            message.error('批量下载失败');
        } finally {
            setBatchDownloading(false);
        }
    }, [batchDownloading, projectId, selectedItems]);

    const handleBatchApply = useCallback(async () => {
        if (batchApplying) return;
        if (!selectedItems.length) {
            message.warning('请先选择图片');
            return;
        }

        try {
            setBatchApplying(true);
            const urls = selectedItems.map((item) => item.url).filter(Boolean);
            await onBatchSend?.(urls);
            onClose?.();
        } catch (error) {
            message.error('应用到画布失败');
        } finally {
            setBatchApplying(false);
        }
    }, [batchApplying, onBatchSend, onClose, selectedItems]);

    const handleSendToCanvas = useCallback((url) => {
        onSend?.(url);
        onClose?.();
    }, [onClose, onSend]);

    const handleDownloadItem = useCallback(async (item) => {
        const fallbackUrl = item?.url || '';
        if (!fallbackUrl) return;

        const resolvedUrls = await resolveDownloadUrlsByItems(projectId, [item]);
        await triggerDownloadByUrl(resolvedUrls[0] || fallbackUrl);
    }, [projectId]);

    const renderPreviewToolbar = useCallback((originalNode, info) => {
        if (!React.isValidElement(originalNode)) return originalNode;

        const currentIndex = typeof info?.current === 'number'
            ? info.current
            : (previewIndex >= 0 ? previewIndex : 0);
        const currentItem = items[currentIndex] || null;
        const originalChildren = React.Children.toArray(originalNode.props.children);
        const filteredChildren = originalChildren.slice(4);

        filteredChildren.push(
            <li
                key="download-current"
                className={`ant-image-preview-operations-operation ${styles.previewDownloadOperation}`}
            >
                <button
                    type="button"
                    className={styles.previewDownloadButton}
                    aria-label="下载当前图片"
                    disabled={!currentItem?.url}
                    onClick={async (event) => {
                        event.stopPropagation();
                        if (!currentItem) return;
                        await handleDownloadItem(currentItem);
                    }}
                >
                    <DownloadOutlined />
                </button>
            </li>,
        );

        return React.cloneElement(originalNode, undefined, filteredChildren);
    }, [handleDownloadItem, items, previewIndex]);

    const allSelected = totalKeys.length > 0 && selectedKeys.length === totalKeys.length;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1250}
            destroyOnClose={false}
            className={styles.modal}
            title={<span className={styles.title}>生成记录</span>}
        >
            <div className={styles.toolbar}>
                {batchMode ? (
                    <div className={styles.batchToolbar}>
                        <div className={styles.batchCount}>已选图片：{selectedKeys.length}张</div>
                        <div className={styles.batchBar}>
                            <button
                                type="button"
                                className={styles.batchAction}
                                onClick={handleBatchDownload}
                                disabled={!selectedKeys.length || batchDownloading}
                            >
                                {batchDownloading ? (
                                    <LoadingOutlined className={styles.loadingIcon} />
                                ) : (
                                    <img src={DOWNLOAD_ICON} alt="" width={16} height={16} draggable={false} />
                                )}
                                <span>下载</span>
                            </button>
                            <button
                                type="button"
                                className={styles.batchAction}
                                onClick={handleBatchApply}
                                disabled={!selectedKeys.length || batchApplying}
                            >
                                {batchApplying ? (
                                    <LoadingOutlined className={styles.loadingIcon} />
                                ) : (
                                    <img src={APPLY_TO_CANVAS_ICON} alt="" width={16} height={16} draggable={false} />
                                )}
                                <span>应用到画布</span>
                            </button>
                            <button
                                type="button"
                                className={styles.batchAction}
                                onClick={handleSelectAll}
                            >
                                {allSelected ? '取消全选' : '全选'}
                            </button>
                            <button
                                type="button"
                                className={styles.batchAction}
                                onClick={handleCancelBatchMode}
                            >
                                取消
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        type="button"
                        className={styles.batchEntry}
                        onClick={handleToggleBatchMode}
                    >
                        <span className={styles.batchEntryIcon} aria-hidden="true" />
                        <span>批量操作</span>
                    </button>
                )}
            </div>

            <div
                ref={scrollRef}
                className={styles.content}
                onScroll={handleScroll}
            >
                {groupedItems.map((group) => (
                    <section key={group.key} className={styles.section}>
                        <h3 className={styles.sectionTitle}>{group.title}</h3>
                        <div className={styles.grid}>
                            {group.items.map((item) => {
                                const itemKey = getItemKey(item);
                                const checked = selectedKeys.includes(itemKey);

                                return (
                                    <div
                                        key={itemKey}
                                        className={`${styles.card} ${batchMode ? styles.cardSelectable : ''} ${checked ? styles.cardSelected : ''}`}
                                        onClick={batchMode ? () => handleToggleSelect(itemKey) : undefined}
                                        role={batchMode ? 'button' : undefined}
                                        tabIndex={batchMode ? 0 : undefined}
                                        onKeyDown={batchMode ? (event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleToggleSelect(itemKey);
                                            }
                                        } : undefined}
                                    >
                                        {batchMode ? (
                                            <label className={styles.checkboxWrap}>
                                                <Checkbox
                                                    checked={checked}
                                                    onClick={(event) => event.stopPropagation()}
                                                    onChange={() => handleToggleSelect(itemKey)}
                                                />
                                            </label>
                                        ) : null}

                                        {batchMode ? (
                                            <img
                                                src={Utils.getImgThumbnail(item.url)}
                                                alt=""
                                                className={styles.image}
                                                draggable={false}
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.previewImageButton}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    const nextIndex = items.findIndex((historyItem) => (
                                                        getItemKey(historyItem) === itemKey
                                                    ));
                                                    setPreviewIndex(nextIndex >= 0 ? nextIndex : 0);
                                                }}
                                            >
                                                <img
                                                    src={Utils.getImgThumbnail(item.url)}
                                                    alt=""
                                                    className={styles.previewImage}
                                                    draggable={false}
                                                />
                                            </button>
                                        )}

                                        {!batchMode ? (
                                            <div className={styles.actions}>
                                                <Tooltip title="应用到画布">
                                                    <button
                                                        type="button"
                                                        className={styles.actionBtn}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleSendToCanvas(item.url);
                                                        }}
                                                    >
                                                        <img src={APPLY_TO_CANVAS_ICON} alt="" width={16} height={16} draggable={false} />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip title="下载">
                                                    <button
                                                        type="button"
                                                        className={styles.actionBtn}
                                                        onClick={async (event) => {
                                                            event.stopPropagation();
                                                            await handleDownloadItem(item);
                                                        }}
                                                    >
                                                        <img src={DOWNLOAD_ICON} alt="" width={16} height={16} draggable={false} />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip title="查看大图">
                                                    <button
                                                        type="button"
                                                        className={styles.actionBtn}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            const nextIndex = items.findIndex((historyItem) => (
                                                                getItemKey(historyItem) === itemKey
                                                            ));
                                                            setPreviewIndex(nextIndex >= 0 ? nextIndex : 0);
                                                        }}
                                                    >
                                                        <ExpandOutlined className={styles.actionIcon} />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ))}

                {loading ? (
                    <div className={styles.loading}>
                        <Spin />
                    </div>
                ) : null}

                {!loading && initialized && items.length === 0 ? (
                    <div className={styles.empty}>暂无生成记录</div>
                ) : null}
            </div>
            <div className={styles.hiddenPreview}>
                <Image.PreviewGroup
                    preview={{
                        visible: previewIndex >= 0,
                        current: previewIndex >= 0 ? previewIndex : 0,
                        toolbarRender: renderPreviewToolbar,
                        onChange: (current) => {
                            setPreviewIndex(current);
                        },
                        onVisibleChange: (visible) => {
                            if (!visible) {
                                setPreviewIndex(-1);
                            }
                        },
                    }}
                >
                    {previewUrls.map((url, index) => (
                        <Image key={`${url}-${index}`} src={url} />
                    ))}
                </Image.PreviewGroup>
            </div>
        </Modal>
    );
};

export default GenerationHistoryModal;
